import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewEncapsulation,
  OnInit,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ChangeDetectorRef,
  ElementRef,
  inject,
} from '@angular/core';
import { NgStyle } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { HighchartsChartModule } from 'highcharts-angular';
import mapboxgl from 'mapbox-gl';
import { MapaFacilV2Component } from '../../auxiliares/mapbox/mapa-facil-v2.component';
import {
  MapaFacilV2Service,
  MapaFacilV2Data,
} from '../../auxiliares/mapbox/mapa-facil-v2.service';
import { MapaFacilData } from '../../auxiliares/mapa-facil/interfaces';
import { ContentCardComponent } from '../content-card/content-card.component';
import { LoadingComponent } from '../loading/loading.component';
import { EmptyStateComponent } from '../empty-state/empty-state.component';
import { CardColor } from '../componentes.interfaces';

export interface MapCardConfig {
  title?: string;
  subtitle?: string;
  icon?: string;
  color?: CardColor;
  showShadow?: boolean;
  margin?: string;
  width?: string;
  height?: string;
  mapOptions?: MapaFacilData;
  nombreMap?: string;
  loading?: boolean;
  disablePopups?: boolean;
}

@Component({
  selector: 'app-map-card',
  standalone: true,
  imports: [
    NgStyle,
    MatCardModule,
    HighchartsChartModule,
    MapaFacilV2Component,
    ContentCardComponent,
    LoadingComponent,
    EmptyStateComponent,
  ],
  templateUrl: './map-card.component.html',
  styleUrls: ['./map-card.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class MapCardComponent implements OnInit, OnChanges, OnDestroy {
  private cdr = inject(ChangeDetectorRef);
  private mapaFacilV2Service = inject(MapaFacilV2Service);

  private map: mapboxgl.Map | null = null;
  private host = inject(ElementRef<HTMLElement>);
  /**
   * Mapbox solo se redimensiona con el resize de la ventana. Cuando el
   * contenedor cambia de tamaño sin que cambie la ventana (un alto que llega
   * después, una columna que se estira), se le avisa al mapa directamente.
   */
  private observadorTamano?: ResizeObserver;
  private avisoPendiente = false;

  /** Configuración completa mediante objeto (opcional) */
  @Input() public set config(value: MapCardConfig) {
    if (value) {
      this._config = value;
      this.applyConfig();
      this.updateMapIfReady();
    }
  }

  private _config: MapCardConfig = {};

  /** Título de la tarjeta */
  @Input() public title = '';

  /** Subtítulo de la tarjeta */
  @Input() public subtitle = '';

  /** Icono de Material Icons */
  @Input() public icon = 'location_on';

  /** Color del título y bordes */
  @Input() public color: CardColor = 'blue';

  /** Mostrar sombra en la tarjeta */
  @Input() public showShadow = true;

  /** Margen personalizado */
  @Input() public margin: string;

  /** Ancho personalizado */
  @Input() public width: string;

  /** Alto personalizado */
  @Input() public height: string;

  /** Opciones de Mapa */
  @Input() public mapOptions: MapaFacilData;

  /** Nombre del mapa para guardar datos */
  @Input() public nombreMap: string;

  /** Estado de carga */
  @Input() public loading = false;

  /** Deshabilitar popups en el mapa */
  @Input() public disablePopups = true;

  /** Mostrar nombre del marker en el mapa */
  @Input() public mostrarNombreMarkers = true;

  /** Evento cuando se hace click en una entidad del mapa */
  @Output() public entityClick = new EventEmitter<any>();

  // Último título y subtítulo que vinieron del config. Si el valor actual es
  // ese (o está vacío), viene del config y se actualiza con cada config nuevo
  // (la dirección cambia en cada refresco); si es otro, lo puso un @Input y
  // gana. Antes se aplicaban una sola vez y quedaban congelados.
  private tituloDeConfig?: string;
  private subtituloDeConfig?: string;

  /**
   * Aplica la configuración del objeto config a las propiedades individuales.
   */
  private applyConfig(): void {
    // 'in' y no '!== undefined': un config que trae la clave vacía (un reporte
    // sin fecha o sin dirección) tiene que limpiar el valor anterior.
    if (
      'title' in this._config &&
      (this.title === '' || this.title === this.tituloDeConfig)
    ) {
      this.title = this._config.title ?? '';
      this.tituloDeConfig = this.title;
    }
    if (
      'subtitle' in this._config &&
      (this.subtitle === '' || this.subtitle === this.subtituloDeConfig)
    ) {
      this.subtitle = this._config.subtitle ?? '';
      this.subtituloDeConfig = this.subtitle;
    }
    if (this._config.icon !== undefined && this.icon === 'bar_chart') {
      this.icon = this._config.icon;
    }
    if (this._config.color !== undefined && this.color === 'blue') {
      this.color = this._config.color;
    }
    if (this._config.showShadow !== undefined && this.showShadow === true) {
      this.showShadow = this._config.showShadow;
    }
    if (this._config.margin !== undefined && this.margin === undefined) {
      this.margin = this._config.margin;
    }
    if (this._config.width !== undefined && this.width === undefined) {
      this.width = this._config.width;
    }
    if (this._config.height !== undefined && this.height === undefined) {
      this.height = this._config.height;
    }
    // `mapOptions` es DATO, no configuración inicial: se aplica cada vez que
    // viene en el config. Las demás guardas de este método existen para que un
    // @Input individual le gane al config, pero acá la guarda `=== undefined`
    // volvía al mapa "apply once": la primera config seteaba el valor y toda
    // actualización posterior se descartaba en silencio.
    //
    // Concreto: en detalles-vehiculo-info, dataDelmapa() se llama dos veces —
    // primero con el marker del último reporte y después con la polilínea del
    // viaje en curso. La segunda siempre perdía, así que del viaje sólo se veía
    // el punto actual.
    //
    // El path del binding directo `[mapOptions]` no pasa por acá; lo sigue
    // manejando ngOnChanges.
    if (this._config.mapOptions !== undefined) {
      this.mapOptions = this._config.mapOptions;
    }
    if (this._config.nombreMap !== undefined && this.nombreMap === undefined) {
      this.nombreMap = this._config.nombreMap;
    }
    if (this._config.loading !== undefined && this.loading === false) {
      this.loading = this._config.loading;
    }
    if (
      this._config.disablePopups !== undefined &&
      this.disablePopups === true
    ) {
      this.disablePopups = this._config.disablePopups;
    }
  }

  /**
   * Callback cuando el mapa está listo
   */
  public onMapReady(map: mapboxgl.Map): void {
    this.map = map;
    this.updateMapIfReady();
    this.observadorTamano?.disconnect();
    this.observadorTamano = new ResizeObserver(() => {
      if (this.avisoPendiente) return;
      this.avisoPendiente = true;
      requestAnimationFrame(() => {
        this.avisoPendiente = false;
        this.map?.resize();
      });
    });
    this.observadorTamano.observe(this.host.nativeElement);
  }

  /**
   * Actualiza el mapa si está listo
   */
  private updateMapIfReady(): void {
    if (!this.map || !this.mapOptions) {
      return;
    }

    // IMPORTANTE: Limpiar datos anteriores para evitar "contaminación" entre mapas pequeños
    // (el servicio es singleton y los datos se comparten entre instancias)
    this.mapaFacilV2Service.clearAllData();

    // CRÍTICO: Limpiar caché para evitar que datos de este mapa pequeño se mezclen
    // con el mapa general al cambiar de estilo
    this.mapaFacilV2Service.clearCache();

    // Convertir MapaFacilData (legacy) a MapaFacilV2Data (nuevo)
    const v2Data = this.convertToV2Data(this.mapOptions);

    // Actualizar datos en el mapa
    this.mapaFacilV2Service.updateData(v2Data);

    // Resize del mapa
    setTimeout(() => {
      this.map?.resize();
    }, 100);
  }

  /**
   * Convierte MapaFacilData (formato legacy) a MapaFacilV2Data (formato nuevo)
   *
   * Esta función mapea los markers genéricos del formato legacy a las estructuras
   * específicas que espera cada entity manager.
   */
  private convertToV2Data(legacyData: MapaFacilData): MapaFacilV2Data {
    const v2Data: MapaFacilV2Data = {};

    // Convertir markers genéricos a arrays específicos por tipo
    if (legacyData.markers && legacyData.markers.length > 0) {
      const vehiculosArray: any[] = [];
      const colectivosArray: any[] = [];
      const trackersArray: any[] = [];
      const activosArray: any[] = [];
      const luminariasArray: any[] = [];
      const alarmasArray: any[] = [];
      const gatewaysArray: any[] = [];
      const sirenasArray: any[] = [];
      const paradasArray: any[] = [];
      const puntosArray: any[] = [];
      const emergenciasMedicasArray: any[] = [];
      const ambulanciasArray: any[] = [];
      const hospitalesArray: any[] = [];
      const centrosAtencionArray: any[] = [];

      for (const marker of legacyData.markers) {
        // Clasificar por tipo y crear la estructura que espera cada manager
        switch (marker.label) {
          // GRUPO 1: Reportes de Tracker (valores.geojson.coordinates)
          case 'vehiculo':
          case 'colectivo':
          case 'tracker':
            const reporteData = {
              _id: marker.id,
              valores: {
                geojson: {
                  type: 'Point',
                  coordinates: marker.coor,
                },
                orientacion: marker.orientation,
              },
              ...marker.data, // Incluir datos adicionales del reporte
            };
            if (marker.label === 'vehiculo') vehiculosArray.push(reporteData);
            else if (marker.label === 'colectivo')
              colectivosArray.push(reporteData);
            else trackersArray.push(reporteData);
            break;

          // GRUPO 2: Array directo (coordenadas)
          case 'activo':
          case 'parada':
          case 'sirena':
          case 'emergenciaMedica':
            const coordenadasData = {
              _id: marker.id,
              coordenadas: marker.coor,
              ...marker.data,
            };
            if (marker.label === 'activo') activosArray.push(coordenadasData);
            else if (marker.label === 'parada')
              paradasArray.push(coordenadasData);
            else if (marker.label === 'sirena')
              sirenasArray.push(coordenadasData);
            else if (marker.label === 'emergenciaMedica')
              emergenciasMedicasArray.push(coordenadasData);
            break;

          // GRUPO 3: ubicacion.coordinates (sin geojson wrapper)
          case 'luminaria':
          case 'gateway':
            const ubicacionData = {
              _id: marker.id,
              ubicacion: {
                type: 'Point',
                coordinates: marker.coor,
              },
              ...marker.data,
            };
            if (marker.label === 'luminaria')
              luminariasArray.push(ubicacionData);
            else gatewaysArray.push(ubicacionData);
            break;

          // GRUPO 4: ubicacion.geojson.coordinates
          case 'hospital':
            hospitalesArray.push({
              _id: marker.id,
              ubicacion: {
                geojson: {
                  type: 'Point',
                  coordinates: marker.coor,
                },
              },
              ...marker.data,
            });
            break;

          // GRUPO 5: domicilio.geojson.coordinates
          case 'alarma':
            alarmasArray.push({
              _id: marker.id,
              domicilio: {
                geojson: {
                  type: 'Point',
                  coordinates: marker.coor,
                },
              },
              ...marker.data,
            });
            break;

          // GRUPO 5b: puntoSimple (puntos genéricos sin tipo específico)
          case 'puntoSimple':
            puntosArray.push({
              _id: marker.id,
              coordenadas: marker.coor,
              text: marker.text,
              ...marker.data,
            });
            break;

          // GRUPO 6: ultimoReporte.valores.geojson.coordinates
          case 'ambulancia':
            const conReporteData = {
              _id: marker.id,
              ultimoReporte: {
                valores: {
                  geojson: {
                    type: 'Point',
                    coordinates: marker.coor,
                  },
                  orientacion: marker.orientation,
                },
              },
              ...marker.data,
            };
            ambulanciasArray.push(conReporteData);
            break;

          // GRUPO 7: circuloArea.center
          case 'centroMedico':
            centrosAtencionArray.push({
              _id: marker.id,
              circuloArea: {
                center: marker.coor,
              },
              ...marker.data,
            });
            break;

          default:
            console.warn(`⚠️ Tipo de marker desconocido: ${marker.label}`);
        }
      }

      // Asignar arrays no vacíos
      if (vehiculosArray.length > 0) v2Data.vehiculos = vehiculosArray;
      if (colectivosArray.length > 0) v2Data.colectivos = colectivosArray;
      if (trackersArray.length > 0) v2Data.trackers = trackersArray;
      if (activosArray.length > 0) v2Data.activos = activosArray;
      if (luminariasArray.length > 0) v2Data.luminarias = luminariasArray;
      if (alarmasArray.length > 0) v2Data.alarmas = alarmasArray;
      if (gatewaysArray.length > 0) v2Data.gateways = gatewaysArray;
      if (sirenasArray.length > 0) v2Data.sirenas = sirenasArray;
      if (paradasArray.length > 0) v2Data.paradas = paradasArray;
      if (puntosArray.length > 0) v2Data.puntos = puntosArray;
      if (emergenciasMedicasArray.length > 0)
        v2Data.emergenciasMedicas = emergenciasMedicasArray;
      if (ambulanciasArray.length > 0) v2Data.ambulancias = ambulanciasArray;
      if (hospitalesArray.length > 0) v2Data.hospitales = hospitalesArray;
      if (centrosAtencionArray.length > 0)
        v2Data.centrosAtencion = centrosAtencionArray;
    }

    // Polylines se pueden pasar directamente (formato compatible)
    if (legacyData.polylines) {
      v2Data.polylines = legacyData.polylines;
    }

    // Polygons se pueden pasar directamente (formato compatible)
    if (legacyData.polygons) {
      v2Data.polygons = legacyData.polygons;
    }

    // Heatmaps requieren conversión de formato
    if (legacyData.heatmaps && legacyData.heatmaps.length > 0) {
      const heatmap = legacyData.heatmaps[0]; // Tomar el primero
      if (heatmap.points) {
        v2Data.heatmap = {
          points: heatmap.points,
          radius: heatmap.radius,
        };
      }
    }

    // Center y zoom
    if (legacyData.center) {
      v2Data.center = legacyData.center;
    }

    if (legacyData.zoom) {
      v2Data.zoom = legacyData.zoom;
    }

    return v2Data;
  }

  ngOnInit(): void {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['mapOptions']) {
      this.updateMapIfReady();
    }
  }

  ngOnDestroy(): void {
    this.observadorTamano?.disconnect();
    // IMPORTANTE: Limpiar caché cuando se destruye el componente (modal/detalle se cierra)
    // para evitar que datos de este mapa pequeño contaminen el mapa general
    this.mapaFacilV2Service.clearCache();
    this.mapaFacilV2Service.clearAllData();
  }

  /**
   * Maneja el click en una entidad del mapa
   */
  onEntityClick(entity: any): void {
    this.entityClick.emit(entity);
  }

  /**
   * Obtiene la instancia del mapa de Mapbox
   * Útil para operaciones directas sobre el mapa desde el componente padre
   */
  public getMap(): mapboxgl.Map | null {
    return this.map;
  }
}
